import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTwitter, FaAtom, FaMapMarkerAlt, FaQuoteLeft, FaQuoteRight, FaCheckCircle, FaLinkedin } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  animation: ${fadeIn} 1s ease-out;
`;

const ProfileSection = styled.div`
  flex: 1;
  padding-right: 20px;
  animation: ${fadeIn} 1s ease-out;
`;

const ProjectsSection = styled.div`
  flex: 3;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  padding-bottom: 40px;
`;

const ProfileImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  animation: ${float} 6s ease-in-out infinite;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;

  a {
    font-size: 24px;
    transition: all 0.3s ease;
    animation: ${fadeIn} 1s ease-out;

    &:hover {
      opacity: 0.8;
      transform: translateY(-3px);
    }
  }

  .twitter-icon {
    color: #1DA1F2;
  }

  .linkedin-icon {
    color: #0077B5;
  }
`;

const ProjectCard = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 300px;
  animation: ${fadeIn} 1s ease-out;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #237fb7, #2196f3);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    box-shadow: 0 8px 16px rgba(0,0,0,0.15);
    transform: translateY(-5px);

    &:before {
      transform: scaleX(1);
    }
  }

  h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
      animation: ${float} 3s ease-in-out infinite;
    }
  }
`;

const ProjectButton = styled.a`
  display: inline-block;
  background-color: #237fb7;
  color: white;
  padding: 8px 16px;
  text-decoration: none;
  border-radius: 4px;
  margin-top: 10px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(35, 127, 183, 0.3);

    &:after {
      left: 100%;
    }
  }
`;

const StatusIndicator = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  margin-left: 10px;
  background-color: ${props => 
    props.$status === 'LIVE' ? '#4CAF50' :
    props.$status === 'BUILDING' ? '#FFA500' :
    props.$status === 'ACQUIRED' ? '#1E90FF' : '#808080'
  };
  color: white;
  animation: ${pulse} 2s infinite;
`;

const HeadlineContainer = styled.div`
  text-align: center;
  margin-bottom: 50px;
  padding: 20px 0;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, #237fb7, #2196f3, #237fb7);
    background-size: 200% 200%;
    animation: ${gradientAnimation} 3s ease infinite;
  }
`;

const Headline = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin: 20px 0;
  text-transform: uppercase;
  letter-spacing: 3px;
  font-weight: 800;
  color: #000;
`;

const SubHeadline = styled.p`
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  animation: ${fadeIn} 1s ease-out;
`;

const MainContent = styled.div`
  display: flex;
`;

const Quote = styled.p`
  font-style: italic;
  position: relative;
  padding: 10px 0;
  
  svg {
    color: #237fb7;
    opacity: 0.3;
    font-size: 16px;
    animation: ${float} 3s ease-in-out infinite;
    
    &:first-child {
      margin-right: 5px;
    }
    
    &:last-child {
      margin-left: 5px;
    }
  }
`;

const MemoizedProjectCard = React.memo(({ project }) => (
  <ProjectCard>
    <h3><project.icon /> {project.title}</h3>
    <p>{project.description}</p>
    <meta name="description" content={`${project.title} - ${project.description}`} />
    <title>{`${project.title} | Arbi Oganesyan's Projects`}</title>
    {project.date && <p><strong>Started:</strong> {new Date(project.date).toLocaleDateString()}</p>}
    {project.status === 'ACQUIRED' && project.acquiredDate && (
      <p><strong>Acquired:</strong> {new Date(project.acquiredDate).toLocaleDateString()}</p>
    )}
    <div style={{ marginTop: 'auto' }}>
      <ProjectButton href={project.link} target="_blank" rel="noopener noreferrer">Visit Site</ProjectButton>
      <StatusIndicator $status={project.status}>{project.status}</StatusIndicator>
    </div>
  </ProjectCard>
));

const PersonalWebsite = () => {
  const [projects, setProjects] = React.useState([
    {
      id: 'explain-this',
      title: 'ExplainThis.dev',
      icon: FaAtom,
      description: 'Transform complex code into clear explanations with AI analysis.',
      status: 'LIVE',
      link: 'https://explainthis.dev',
      date: '05-27-2025'
    },
    {
      id: 'blacklist-check',
      title: 'BlackList Shield',
      icon: FaAtom,
      description: 'Verify DNS records, monitor blacklists, and secure email with MX, SPF, DKIM, and DMARC checks.',
      status: 'LIVE',
      link: 'https://blacklistshield.com',
      date: '05-01-2025'
    },
    {
      id: 'cover-joy',
      title: 'Cover Joy',
      icon: FaAtom,
      description: 'Cover letters for your next job done right with AI. Paste resume, paste job description, get cover letter.',
      status: 'LIVE',
      link: 'https://coverjoy.xyz',
      date: '08-24-2024'
    },
    {
      id: 'sql-genie',
      title: 'SQL Genie',
      icon: FaAtom,
      description: 'Your Advanced AI-Powered SQL Assistant. Simply explain what you want to achieve in plain English and allow SQL Genie to write the fully query for you.',
      status: 'LIVE',
      link: 'https://sqlgenie.app',
      date: '08-20-2024'
    },     
    {
      id: 'onehopweb',
      title: 'OneHopWeb',
      icon: FaAtom,
      description: 'OneHopWeb is a web hosting company that provides affordable cPanel shared and reseller hosting.',
      status: 'ACQUIRED',
      link: 'https://www.prlog.org/12564727-blue-bell-web-hosting-llc-acquires-onehopwebcom.html',
      date: '12-14-2009',
      acquiredDate: '07-10-2016',
    },
    {
      id: 'evolans',
      title: 'Evolans LLC',
      icon: FaAtom,
      description: 'EVOLANS LLC is another gaming community dedicated to providing game server and web hosting services.',
      status: 'ACQUIRED',
      link: 'https://mergr.com/transaction/onehopweb-acquires-evolans',
      acquiredDate: '01-23-2008',
    },
    {
      id: 'unitedfrag',
      title: 'UnitedFrag',
      icon: FaAtom,
      description: 'UnitedFrag is a gaming community dedicated to providing game server and web hosting services on the internet.',
      status: 'ACQUIRED',
      link: 'https://mergr.com/transaction/onehopweb-acquires-unitedfrag',
      date: '08-10-2005',
      acquiredDate: '09-07-2007',
    },
  ]);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(projects);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setProjects(items);
  };

  return (
    <Container>
      <HeadlineContainer>
        <Headline>Follow My Journey</Headline>
        <SubHeadline>
          Explore my entrepreneurial path through various projects and ventures, 
          each representing a unique chapter in my ongoing tech journey.
        </SubHeadline>
      </HeadlineContainer>
      <MainContent>
        <ProfileSection>
          <ProfileImage src="/img/me.jpg" alt="Profile" />
          <h1>Arbi</h1>
          <p><FaMapMarkerAlt /> Los Angeles, CA</p>
          <p><FaCheckCircle /> Ninja by day, entrepreneur by night. </p>
          <Quote>
            <FaQuoteLeft /> You don't choose your passions; your passions choose you. <FaQuoteRight />
          </Quote>
          <SocialIcons>
            <a href="https://twitter.com/arbioganesyan" target="_blank" rel="noopener noreferrer" className="twitter-icon">
              <FaTwitter />
            </a>
            <a href="https://www.linkedin.com/in/oganesyanarbi/" target="_blank" rel="noopener noreferrer" className="linkedin-icon">
              <FaLinkedin />
            </a>
          </SocialIcons>
        </ProfileSection>
        <ProjectsSection>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="projects" direction="horizontal">
              {(provided) => (
                <div 
                  {...provided.droppableProps} 
                  ref={provided.innerRef}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '30px'
                  }}
                >
                  {projects.map((project, index) => (
                    <Draggable key={project.id} draggableId={project.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <MemoizedProjectCard project={project} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </ProjectsSection>
      </MainContent>
    </Container>
  );
};

export default PersonalWebsite;